// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getReturnOrderFiles } from '@/return-order/api/get-return-order-files';
import { returnOrderFilesQueryKey } from '@/return-order/query-keys';
import { useQuery } from '@tanstack/react-query';

interface UseReturnOrderFilesOptions {
  /** The key of the return order to fetch files for */
  returnOrderKey: string;
}

/**
 * Hook to fetch files associated with a return order.
 * Uses a regular query (not suspense) to allow graceful error handling
 * without crashing the entire page.
 *
 * @param options - Options including the return order key
 * @returns The files, loading state, error state, and query methods
 */
export function useReturnOrderFiles(options: UseReturnOrderFilesOptions) {
  const { data, isLoading, isError, error, refetch, ...query } = useQuery({
    queryKey: returnOrderFilesQueryKey(options.returnOrderKey),
    queryFn: () => getReturnOrderFiles(options.returnOrderKey),
  });

  return {
    files: data?.data ?? [],
    isLoading,
    isError,
    error,
    refetch,
    ...query,
  };
}
